import type { RawDraftEntity } from 'draft-js';

const IMAGE_TAG_NAME = 'IMG';
const DIV_NODE_NAME = 'div';
const DEFAULT_IMAGE_ALIGNMENT = 'none';

export const getStylePropsMap = (node: HTMLElement): Map<string, string> => {
  const style = node.getAttribute('style');
  const tokens = style
    ?.split(';')
    .filter(prop => !!prop)
    .map<[string, string]>(prop => {
      const [key, value] = prop.split(':').map(token => token.trim());
      return [key, value];
    });

  const tokenMap = new Map<string, string>(tokens);

  return tokenMap;
};

export const getImageWrapperAlignment = (wrapper: HTMLElement): string => {
  const wrapperStyleProps = getStylePropsMap(wrapper);
  const alignment = wrapperStyleProps.get('text-align');

  if (!alignment) return DEFAULT_IMAGE_ALIGNMENT;

  return alignment === 'center' ? DEFAULT_IMAGE_ALIGNMENT : alignment;
};

export const getImageSize = (image: HTMLImageElement): { width?: string; height?: string } => {
  const imageStyleProps = getStylePropsMap(image);
  const width = imageStyleProps.get('width');
  const height = imageStyleProps.get('height');

  return { width, height };
};

export const getWrappedImage = (nodeName: string, node: HTMLElement): HTMLImageElement | null => {
  if (nodeName !== DIV_NODE_NAME) return null;
  if (node.childElementCount !== 1) return null;

  const child = node.firstElementChild;

  if (!child) return null;
  if (child.tagName !== IMAGE_TAG_NAME) return null;

  return child as HTMLImageElement;
};

export const renderImageWithWrapper = (nodeName: string, node: HTMLElement): RawDraftEntity | undefined => {
  if (nodeName === DIV_NODE_NAME) {
    const wrappedImage = getWrappedImage(nodeName, node);

    if (wrappedImage) {
      const wrapperAlignment = getImageWrapperAlignment(node);
      const { width, height } = getImageSize(wrappedImage);

      return {
        data: {
          alignment: wrapperAlignment,
          src: wrappedImage.src,
          alt: wrappedImage.alt,
          height: height,
          width: width,
        },
        mutability: 'MUTABLE',
        type: 'IMAGE',
      };
    }
  }

  return;
};

export const customChunkRenderer = (nodeName: string, node: HTMLElement): RawDraftEntity | undefined => {
  // Images are wrapped in divs for alignment but these wrappers are not correctly supported by htmlToDraft. renderImageWithWrapper provides a fix for that.
  const wrappedImage = renderImageWithWrapper(nodeName, node);
  if (wrappedImage) return wrappedImage;

  return;
};
